import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ActionIcon, Box, Skeleton, UnstyledButton } from "@mantine/core";
import {
  IconAward,
  IconCheck,
  IconChevronRight,
  IconStar,
  IconX,
} from "@tabler/icons-react";
import { useApp } from "../../../../../context/AppContext";
import { useLoyaltyProgram } from "../../../../../hooks/useLoyaltyProgram";
import {
  AvailableLoyaltyReward,
  ClientLoyaltyResponse,
} from "../../../../../types/loyalty";
import {
  GetTransactionsResponse,
  TransactionResponse,
} from "../../../../../types/transactions";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import LoyaltyRewardPickerModal, {
  LoyaltyRewardPick,
} from "../../../../rewards/LoyaltyRewardPickerModal";
import styles from "./RewardsPromotionsPanel.module.css";

function num(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

interface RewardsPromotionsPanelProps {
  transaction: GetTransactionsResponse | TransactionResponse;
  /** Rewards currently staged for redemption at Charge. Lifted to
   *  TransactionDetailModal so the state survives the Appointment Details
   *  → Checkout screen swap. */
  stagedRewards: LoyaltyRewardPick[];
  onStagedRewardsChange: (picks: LoyaltyRewardPick[]) => void;
}

/**
 * "Rewards & Promotions" section of Appointment Summary (right column).
 *
 * Empty state (image 12): full-width "Browse available rewards" +
 * "Redeem purchased packages" buttons under the eyebrow + points-balance pill.
 *
 * Applied state (image 14): a chip per applied reward with a `×` remove
 * control. Clicking the chip's `×` clears every quantity of that reward from
 * the local staging — the picker can be reopened to change quantities.
 *
 * Hidden entirely when loyalty is disabled, the transaction has no client,
 * or the client's points balance is 0.
 */
export default function RewardsPromotionsPanel({
  transaction,
  stagedRewards,
  onStagedRewardsChange,
}: RewardsPromotionsPanelProps) {
  const { getClientLoyalty, getTransactionAvailableLoyaltyRewards } = useApp();
  const { isEnabled, apiBusinessLocationId } = useLoyaltyProgram();

  const clientId = transaction.client?.id ?? null;
  const transactionId = transaction.transaction.id;

  const [pointsBalance, setPointsBalance] = useState<number | null>(null);
  const [rewards, setRewards] = useState<AvailableLoyaltyReward[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  // Points balance. Degrades to 0 silently on failure (endpoint may not be
  // deployed in every lower env).
  useEffect(() => {
    if (!isEnabled || !clientId) {
      setPointsBalance(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response: ClientLoyaltyResponse | null =
          apiBusinessLocationId != null
            ? await getClientLoyalty(clientId, apiBusinessLocationId)
            : null;
        if (!cancelled)
          setPointsBalance(response?.account?.points_balance ?? 0);
      } catch {
        if (!cancelled) setPointsBalance(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEnabled, clientId, apiBusinessLocationId, getClientLoyalty]);

  // Available (affordable) rewards for this transaction. Empty list still
  // renders the section — staff can see the "no rewards match" state inside
  // the modal.
  useEffect(() => {
    if (!isEnabled || !clientId) {
      setRewards([]);
      return;
    }
    let cancelled = false;
    getTransactionAvailableLoyaltyRewards(transactionId)
      .then((list) => {
        if (!cancelled) setRewards(list ?? []);
      })
      .catch(() => {
        if (!cancelled) setRewards([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    isEnabled,
    clientId,
    transactionId,
    getTransactionAvailableLoyaltyRewards,
  ]);

  const stagedPointsSpent = useMemo(
    () =>
      stagedRewards.reduce(
        (acc, pick) => acc + pick.quantity * num(pick.reward.points_cost),
        0,
      ),
    [stagedRewards],
  );
  const stagedDollarsTotal = useMemo(
    () =>
      stagedRewards.reduce(
        (acc, pick) => acc + pick.quantity * num(pick.reward.applied_value),
        0,
      ),
    [stagedRewards],
  );
  // Balance shown in the pill reflects the local staging so the number
  // matches what the picker's header pill will show if reopened.
  const displayedBalance = Math.max(
    0,
    (pointsBalance ?? 0) - stagedPointsSpent,
  );

  const hasAppliedRewards = stagedRewards.length > 0;

  // Rewards are only stageable while the appointment is still open. Terminal
  // statuses grey the "Browse available rewards" action — no retroactive
  // rewards on completed / declined / cancelled appointments.
  const apptStatus = transaction.transaction.appointment_status;
  const isTerminalAppointment =
    apptStatus === "Completed" ||
    apptStatus === "Cancelled" ||
    apptStatus === "Declined";

  const handleApply = useCallback(
    (picks: LoyaltyRewardPick[]) => {
      onStagedRewardsChange(picks);
    },
    [onStagedRewardsChange],
  );

  // While the balance fetch is in flight we keep the panel mounted with
  // skeleton placeholders (instead of popping it in after the modal has
  // painted). Panel hides for good only when: loyalty off, no client, or
  // the balance resolves to exactly 0 (nothing to redeem).
  const stillLoading = isEnabled && !!clientId && pointsBalance === null;
  const hasBalance =
    isEnabled && !!clientId && pointsBalance !== null && pointsBalance > 0;
  if (!stillLoading && !hasBalance) return null;

  if (stillLoading) {
    return (
      <Box className={styles.section} data-testid="rewards-panel-skeleton">
        <Box className={styles.headerRow}>
          <Box component="span" className={styles.eyebrow}>
            Rewards & Promotions
          </Box>
        </Box>
        <Skeleton height={44} radius="md" />
      </Box>
    );
  }

  // Reward count for the Browse button label. Falls back to a "no
  // rewards" message when the client has nothing they can afford.
  const rewardsCount = rewards.length;
  const browseLabel =
    rewardsCount === 0
      ? "No available Rewards"
      : `Browse Available Rewards (${rewardsCount})`;

  return (
    <>
      <Box className={styles.section}>
        <Box className={styles.headerRow}>
          <Box component="span" className={styles.eyebrow}>
            Rewards & Promotions
          </Box>
          <Box
            component="span"
            className={styles.pointsPill}
            aria-label="Points balance"
          >
            <IconStar size={13} />
            {displayedBalance.toLocaleString("en-US")} pts
          </Box>
        </Box>

        {hasAppliedRewards ? (
          <Box className={styles.appliedChip}>
            <Box className={styles.appliedChipIcon}>
              <IconCheck size={16} />
            </Box>
            <Box className={styles.appliedChipBody}>
              <Box component="span" className={styles.appliedChipName}>
                Redeem {stagedPointsSpent.toLocaleString("en-US")} points for{" "}
                {formatCurrency(stagedDollarsTotal)} off
              </Box>
            </Box>
            <Box component="span" className={styles.appliedChipAmount}>
              −{formatCurrency(stagedDollarsTotal)}
            </Box>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label="Clear applied rewards"
              onClick={() => onStagedRewardsChange([])}
            >
              <IconX size={14} />
            </ActionIcon>
          </Box>
        ) : (
          <UnstyledButton
            className={styles.actionButton}
            onClick={() => setModalOpen(true)}
            disabled={isTerminalAppointment || rewardsCount === 0}
            data-disabled={
              isTerminalAppointment || rewardsCount === 0 || undefined
            }
            style={
              isTerminalAppointment || rewardsCount === 0
                ? { opacity: 0.5, cursor: "not-allowed" }
                : undefined
            }
          >
            <Box
              component="span"
              className={styles.actionButtonLabel}
              style={{ fontWeight: 500, fontSize: 13.5 }}
            >
              <Box className={styles.actionButtonIconTile}>
                <IconAward size={16} stroke={1.9} />
              </Box>
              {browseLabel}
            </Box>
            <Box component="span" className={styles.actionButtonChevron}>
              <IconChevronRight size={16} />
            </Box>
          </UnstyledButton>
        )}
      </Box>

      <LoyaltyRewardPickerModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        rewards={rewards}
        pointsBalance={pointsBalance ?? 0}
        initialPicks={stagedRewards}
        onApply={handleApply}
      />
    </>
  );
}
